import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { CheckCircle2, XCircle, Clock } from 'lucide-react';
import { SiteLayout, useDocumentTitle } from '../components/platform/SiteLayout';
import { Container, Card, Btn, Heading, Spinner } from '../components/platform/ui';
import { subscriptionAPI } from '../utils/auth';
import { formatPrice } from '../lib/pay';
import { useLang } from '../lib/lang';

const POLL_MS = 3000;
const MAX_POLLS = 40; // ~2 минуты

/**
 * Страница, на которую возвращает ЮKassa. Сама по себе она ничего не подтверждает:
 * статус берётся с сервера, который проверил платёж (вебхук / запрос к API ЮKassa).
 */
const PaymentResultPage = () => {
  const { t } = useTranslation();
  const { lang } = useLang();
  useDocumentTitle(t('pl.payment.title'));
  const [params] = useSearchParams();
  const orderId = parseInt(params.get('order_id'), 10);
  const [order, setOrder] = useState(null);
  const [error, setError] = useState(null);
  const [timedOut, setTimedOut] = useState(false);
  const polls = useRef(0);

  useEffect(() => {
    if (!orderId) { setError(t('pl.payment.noOrder')); return undefined; }
    let stop = false;
    let timer;
    const tick = async () => {
      try {
        const o = await subscriptionAPI.getOrder(orderId);
        if (stop) return;
        setOrder(o);
        if (o.status === 'pending' && ++polls.current < MAX_POLLS) {
          timer = setTimeout(tick, POLL_MS);
        } else if (o.status === 'pending') {
          setTimedOut(true);
        }
      } catch (e) {
        if (stop) return;
        setError(e.response?.status === 404 ? t('pl.payment.notFound') : t('pl.payment.statusError'));
      }
    };
    tick();
    return () => { stop = true; clearTimeout(timer); };
  }, [orderId, t]);

  let body;
  if (error) {
    body = (<><XCircle size={44} className="mx-auto text-[#C41E3A] mb-3" /><Heading level={2} className="text-2xl mb-2">{error}</Heading></>);
  } else if (!order) {
    body = <Spinner label={t('pl.payment.checking')} />;
  } else if (order.status === 'completed') {
    body = (
      <>
        <CheckCircle2 size={44} className="mx-auto text-green-700 mb-3" />
        <Heading level={2} className="text-2xl mb-2">{t('pl.payment.paid')}</Heading>
        <p className="text-gray-700">{order.title} — {formatPrice(order.amount, lang)}</p>
        {order.kind === 'tokens' && order.tokens_granted > 0 && <p className="mt-1">{t('pl.payment.granted', { count: order.tokens_granted })}</p>}
        {order.kind === 'course' && <p className="mt-1">{t('pl.payment.courseGranted')}</p>}
      </>
    );
  } else if (order.status === 'pending') {
    body = (
      <>
        <Clock size={44} className="mx-auto text-gray-600 mb-3" />
        <Heading level={2} className="text-2xl mb-2">{timedOut ? t('pl.payment.notConfirmed') : t('pl.payment.waiting')}</Heading>
        <p className="text-gray-700 max-w-md mx-auto">
          {timedOut ? t('pl.payment.notConfirmedText') : t('pl.payment.waitingText')}
        </p>
        {!timedOut && <Spinner label="" />}
      </>
    );
  } else {
    body = (
      <>
        <XCircle size={44} className="mx-auto text-[#C41E3A] mb-3" />
        <Heading level={2} className="text-2xl mb-2">{order.status === 'cancelled' ? t('pl.payment.cancelled') : t('pl.payment.failed')}</Heading>
        <p className="text-gray-700">{t('pl.payment.failedText')}</p>
        {order.failure_reason && <p className="text-sm text-gray-500 mt-2">{t('pl.payment.reason', { reason: order.failure_reason })}</p>}
      </>
    );
  }

  return (
    <SiteLayout>
      <Container className="py-16 max-w-xl">
        <Card className="text-center py-10">
          {body}
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Btn to="/cabinet">{t('pl.payment.toCabinet')}</Btn>
            <Btn to="/cabinet/purchases" variant="secondary">{t('pl.payment.purchases')}</Btn>
            {order && (order.status === 'failed' || order.status === 'cancelled') && <Btn to="/products/cbct-segmentation#tariffs" variant="secondary">{t('pl.payment.retry')}</Btn>}
          </div>
        </Card>
      </Container>
    </SiteLayout>
  );
};

export default PaymentResultPage;
